import { useAuth } from '@/hooks'
import { CategoriaEnum } from '@acme/auth'
import { filterLocaisCentro } from '@acme/common'
import {
    Checkbox,
    InputAdornment,
    ListItemText,
    MenuItem,
    SelectChangeEvent,
    TextField,
} from '@mui/material'
import CircularProgress from '@/global/components/CircularProgress'
import dayjs from 'dayjs'
import { FormField } from 'global/components/Form'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { funcionario, Funcionario } from 'service/api'
import { useStore } from 'store'
import { SelectProps } from './types'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const DEBOUNCE_MS = 300

/**
 * Sem isso a lista de nomes selecionados define a largura mínima do campo e
 * estoura o container (scroll lateral no modal).
 */
const truncaValorSelecionado = {
    minWidth: 0,
    '& .MuiSelect-select': {
        display: 'block',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
} as const

type Props = Omit<
    SelectProps,
    'buttom' | 'isSupervisor' | 'form' | 'defaultValue'
> & {
    checked?: number[]
    onChange?: (ids: number[]) => void
    defaultValues?: number[]
    /** Utiliza filtro de locais e centro de custo */
    useFilter?: boolean
    value?: number[]
    form?: boolean | string
    /** Chamado quando o estado de "Todos" muda */
    onSelectAllChange?: (isAll: boolean) => void
    /** Exibe checkbox "Mostrar demitidos" que inclui rescindidos na lista */
    permitirDemitidos?: boolean
    /** Lista também funcionários rescindidos (necessário em relatórios de período) */
    incluirRescindidos?: boolean
}

export const SelectFuncionarios: React.FC<Props> = ({
    size = 'small',
    form,
    children,
    disabled,
    checked,
    onChange,
    defaultValues = [],
    useFilter = false,
    value,
    width = 260,
    onSelectAllChange,
    permitirDemitidos = false,
    incluirRescindidos = false,
    ...props
}) => {
    const formName = typeof form === 'string' ? form : 'funcionarioIds'
    const {
        decoded: { categoria },
    } = useAuth()
    const formContext = useFormContext()
    const empresaId = useStore(state => state.companyId)
    const centroCustoId = useStore(state => state.costCenterId)
    const locais = useStore(state => state.locaisIds)
    const [competencia, competenciaAtual] = useStore(state => [
        state.competencia,
        state.competenciaAtual,
    ])
    const diasRescisao = useStore(state => state.company?.diasRescisao)

    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [selectAll, setSelectAll] = useState(false)
    const debounceRef = useRef<ReturnType<typeof setTimeout>>()

    const handleSearchChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value
            setSearch(value)
            if (debounceRef.current) clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
                setDebouncedSearch(value)
            }, DEBOUNCE_MS)
        },
        [],
    )

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [])

    const queryParams: Record<string, unknown> = { empresaId }
    if (useFilter && centroCustoId > 0)
        queryParams.centroCustoId = centroCustoId
    if (useFilter && locais.length) queryParams.localId = locais[0]
    if (debouncedSearch) queryParams.search = debouncedSearch

    const {
        data: pages,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = funcionario.useInfiniteScroll({
        queryParams,
        enabled: empresaId > 0,
    })

    const allLoaded = useMemo(
        () => pages?.pages.flatMap(p => p.data) ?? [],
        [pages],
    ) as Funcionario[]

    const exibirApenasAtivos =
        !incluirRescindidos && categoria !== CategoriaEnum.Funcionario
    const [mostrarDemitidos, setMostrarDemitidos] = useState(false)

    const isDemitido = useCallback(
        (obj: Funcionario) => {
            if (!obj.dtRescisao) return false
            const dataRef =
                    competencia === competenciaAtual
                        ? dayjs()
                        : dayjs(competencia).startOf('month'),
                tolerancia = diasRescisao ?? 40

            return dataRef
                .add(tolerancia, 'day')
                .isAfter(dayjs(obj.dtRescisao), 'day')
        },
        [competencia, competenciaAtual, diasRescisao],
    )

    const filtrarFuncionario = useCallback(
        (obj: Funcionario) => {
            if (exibirApenasAtivos && !mostrarDemitidos && isDemitido(obj))
                return false

            if (useFilter === false) return true
            return filterLocaisCentro<typeof obj>(
                locais,
                [centroCustoId!],
                it => it as any,
            )(obj)
        },
        [
            exibirApenasAtivos,
            mostrarDemitidos,
            isDemitido,
            centroCustoId,
            locais,
            useFilter,
        ],
    )
    const data = useMemo(
        () => allLoaded.filter(filtrarFuncionario),
        [allLoaded, filtrarFuncionario],
    )

    const [_employeeIds, _setEmployeeIds] = useState<number[]>(defaultValues)
    const employeeIds = value ? value : _employeeIds
    const setEmployeeIds = (ids: number[]) => {
        _setEmployeeIds(ids)
        onChange?.(ids)
    }

    useEffect(() => {
        if (useFilter && (centroCustoId > 0 || locais.length)) {
            setEmployeeIds([])
            setSelectAll(false)
            onSelectAllChange?.(false)
        }
    }, [centroCustoId, locais, useFilter])

    const { data: todosFuncionarios = [] } = funcionario.useFetch<
        Funcionario[]
    >({
        queryParams: {
            empresaId,
            ...(useFilter && centroCustoId > 0 ? { centroCustoId } : {}),
            ...(useFilter && locais.length ? { localId: locais[0] } : {}),
        } as any,
        enabled: selectAll && empresaId > 0,
    })

    useEffect(() => {
        if (!selectAll) return
        const ids = todosFuncionarios
            .filter(filtrarFuncionario)
            .map(({ id }) => id)
            .filter((id): id is number => id != null && !checked?.includes(id))
        setEmployeeIds(ids)
    }, [selectAll, todosFuncionarios, filtrarFuncionario])

    // Ao desmarcar "Mostrar demitidos", remove da seleção apenas os
    // demitidos conhecidos, evitando seleção fantasma no form. Com "Todos"
    // marcado a recomputação do effect acima já cobre o caso.
    const prevMostrarDemitidos = useRef(false)
    useEffect(() => {
        if (prevMostrarDemitidos.current && !mostrarDemitidos && !selectAll) {
            const demitidos = new Set(
                allLoaded.filter(isDemitido).map(({ id }) => id),
            )
            if (employeeIds.some(id => demitidos.has(id))) {
                setEmployeeIds(employeeIds.filter(id => !demitidos.has(id)))
            }
        }
        prevMostrarDemitidos.current = mostrarDemitidos
    }, [mostrarDemitidos])

    const handleChange = (event: SelectChangeEvent<unknown>) => {
        const {
            target: { value },
        } = event

        // Itens utilitários (busca, "Mostrar demitidos") têm value "" e o
        // Select do MUI ainda dispara onChange ao clicá-los — ignora para
        // não injetar "" na seleção nem derrubar o estado de "Todos".
        if ((value as any[]).includes('')) return

        if ((value as any[]).includes('todos')) {
            if (selectAll) {
                setSelectAll(false)
                setEmployeeIds([])
                onSelectAllChange?.(false)
            } else {
                setSelectAll(true)
                setEmployeeIds([])
                onSelectAllChange?.(true)
            }
        } else {
            setSelectAll(false)
            setEmployeeIds(value as number[])
            onSelectAllChange?.(false)
        }
    }

    useEffect(() => {
        if (form && formContext) {
            formContext.setValue(formName, employeeIds)
        }
    }, [employeeIds])

    const handleScroll = useCallback(
        (event: React.UIEvent<HTMLElement>) => {
            const target = event.currentTarget
            const nearBottom =
                target.scrollHeight - target.scrollTop - target.clientHeight <
                50
            if (nearBottom && hasNextPage && !isFetchingNextPage) {
                fetchNextPage()
            }
        },
        [fetchNextPage, hasNextPage, isFetchingNextPage],
    )

    const MenuPropsWithScroll = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
            onScroll: handleScroll,
        },
    }

    const renderValue = (selected: unknown) => {
        if (selectAll) return 'Todos'

        const ids = (selected as number[]).filter(id => !checked?.includes(id))
        const nomes = data
            .filter(it => ids.includes(it.id))
            .map(({ nome }) => nome)

        const naoCarregados = ids.length - nomes.length
        const texto = (
            naoCarregados > 0 ? [...nomes, `+${naoCarregados}`] : nomes
        ).join(', ')

        return <span className="block max-w-9/10 truncate">{texto}</span>
    }

    const searchField = (
        <MenuItem
            key="employee-search"
            value=""
            onKeyDown={e => e.stopPropagation()}
            onClick={e => e.preventDefault()}
            sx={{ '&.Mui-focusVisible': { backgroundColor: 'transparent' } }}
            className="hover:bg-inherit!"
        >
            <TextField
                size="small"
                placeholder="Buscar funcionário..."
                value={search}
                onChange={handleSearchChange}
                onClick={e => e.stopPropagation()}
                fullWidth
                autoFocus
                InputProps={{
                    endAdornment: isFetchingNextPage ? (
                        <InputAdornment position="end">
                            <CircularProgress size={16} />
                        </InputAdornment>
                    ) : undefined,
                }}
            />
        </MenuItem>
    )

    const todosItem = (
        <MenuItem key="employee-todos" value="todos">
            <Checkbox checked={selectAll} />
            <ListItemText primary="Todos" />
        </MenuItem>
    )

    const demitidosItem =
        permitirDemitidos && exibirApenasAtivos ? (
            <MenuItem
                key="employee-demitidos"
                value=""
                onClick={e => {
                    e.preventDefault()
                    setMostrarDemitidos(v => !v)
                }}
            >
                <Checkbox checked={mostrarDemitidos} />
                <ListItemText primary="Mostrar demitidos" />
            </MenuItem>
        ) : null

    const employeeItems = data.map((obj, index) => (
        <MenuItem
            key={`employee-${index}`}
            value={obj.id}
            disabled={checked?.includes(obj.id)}
        >
            <Checkbox
                checked={
                    selectAll ||
                    checked?.includes(obj.id) ||
                    employeeIds.some(it => it === obj.id)
                }
            />
            <ListItemText
                primary={
                    exibirApenasAtivos && isDemitido(obj)
                        ? `${obj.nome} (Demitido)`
                        : obj.nome
                }
            />
        </MenuItem>
    ))

    const loadingItem = isFetchingNextPage ? (
        <MenuItem key="employee-loading" disabled>
            <div className="flex w-full justify-center">
                <CircularProgress size={20} />
            </div>
        </MenuItem>
    ) : null

    return (
        <div
            className="flex min-w-0 max-w-full items-center gap-2"
            style={{ width }}
        >
            {form ? (
                <FormField
                    name={formName}
                    label="Funcionários"
                    size={size}
                    select
                    disabled={disabled}
                    helperText=" "
                    fullWidth
                    sx={truncaValorSelecionado}
                    {...props}
                    SelectProps={{
                        multiple: true,
                        value: selectAll ? ['todos'] : employeeIds,
                        onChange: handleChange,
                        renderValue,
                        MenuProps: MenuPropsWithScroll,
                    }}
                >
                    {searchField}
                    {children}
                    {demitidosItem}
                    {todosItem}
                    {employeeItems}
                    {loadingItem}
                </FormField>
            ) : (
                <TextField
                    label="Funcionários"
                    variant="filled"
                    sx={truncaValorSelecionado}
                    SelectProps={{
                        multiple: true,
                        value: selectAll ? ['todos'] : employeeIds,
                        onChange: handleChange,
                        renderValue,
                        MenuProps: MenuPropsWithScroll,
                    }}
                    select
                    fullWidth
                    disabled={disabled}
                    size={size}
                    {...props}
                >
                    {searchField}
                    {children}
                    {demitidosItem}
                    {data.length > 0 && todosItem}
                    {employeeItems}
                    {loadingItem}
                </TextField>
            )}
        </div>
    )
}
